import type { SupabaseClient } from "@supabase/supabase-js";
import type { PersistedFile, RepositoryResult } from "./persistence/contracts";
import type { Database } from "./persistence/database.generated";
import { bytesSha256, canonicalJsonSha256 } from "./persistence/supabase/checksum";
import { SupabaseFileRepository } from "./persistence/supabase/file-repository";

export interface HostedAyzeoPdfSourceFilePort {
  store(input: { organisationId: string; auditEngagementId: string; file: File }): Promise<RepositoryResult<PersistedFile>>;
}

const isPdf = (bytes: Uint8Array) => bytes.length >= 5
  && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46 && bytes[4] === 0x2d;

/** Browser-only private-source-file boundary for an operator-selected Ayzeo PDF. */
export function createHostedAyzeoPdfSourceFilePort(client: SupabaseClient<Database>): HostedAyzeoPdfSourceFilePort {
  const files = new SupabaseFileRepository(client);
  return {
    async store({ organisationId, auditEngagementId, file }) {
      const bytes = new Uint8Array(await file.arrayBuffer());
      if (!isPdf(bytes)) {
        return { ok: false, error: { code: "validation_failure", message: "The selected file is not a valid PDF.", retryable: false } };
      }
      const identity = await canonicalJsonSha256({ organisationId, sha256: await bytesSha256(bytes) });
      const fileId = `ayzeo-source-${identity}`;
      const reserved = await files.reserveUpload({
        id: fileId,
        organisationId,
        logicalName: "Ayzeo PDF evidence source",
        originalFilename: file.name,
        bucketId: "organisation-source-files",
        mimeType: "application/pdf",
        bytes,
        visibility: "Internal only",
        metadata: { schemaVersion: 1, data: { evidenceKind: "ayzeo-pdf-source" } },
        idempotencyKey: `ayzeo-source:${identity}:reserve`,
      });
      if (!reserved.ok || reserved.value.status === "Available") return reserved;
      const uploaded = await files.uploadReservedObject(reserved.value, bytes);
      if (!uploaded.ok) return uploaded;
      return files.confirmUpload(reserved.value, bytes, `ayzeo-source:${identity}:confirm`);
    },
  };
}
